import React, { useCallback, useMemo, useState, type JSX } from 'react';
import { Row } from '@harmony/enablers/components/data-grid/data-grid';
import { t } from 'i18next';
import { CSSProperties } from '@harmony/enablers/react/scope-for-react';
import { DividerSeparatedText } from './divider-separated-text';
import { Option, Combobox, DataGrid, Pagination, SearchBox, Text, Button, Icon } from '../providers/harmony-scope/lazy-enablers';
import { commonGridStyle, searchBoxStyle } from '../utils/common-styles';
import { TTextValueTooltip } from './shared-types';
import { usePageContext } from '../utils/page-context';
import { useTrackInteraction } from '../services/logging/hooks';
import { TColumnData, TTableFilters, TableFiltersPanel } from './table-filters-panel';
import { useFilteredRows, useGetPageAmount, useGetPageItems, useGetSortedRows } from './table-hooks';
import { paginatorCombobox } from './custom-mem-table.module.css';

interface Iprops {
  componentName: string;
  rowsData: Row<unknown>[];
  dividedTextData?: TTextValueTooltip[];
  columnsInfo: TColumnData[];
  filterKey?: string;
  rowFormatter?: (row: Row<any>) => JSX.Element;
  isLoading?: boolean;
  selectOnClick?: boolean;
  selectType?: 'single' | 'multiple';
  rowSelectChange?: (selectedRow: Row<any>) => void;
  selectAllChange?: (selected: boolean) => void;
  comandBar?: JSX.Element;
  showFilterBox?: boolean;
}

export const alignRowRightStyle: React.CSSProperties & CSSProperties = {
  textAlign: 'right',
  display: 'block',
};

export const RightAlignedCell = (props: {
  slot: string;
  contentWidth?: number;
  style?: React.CSSProperties & CSSProperties;
  innerStyle?: React.CSSProperties & CSSProperties;
  children: React.ReactNode;
}) => {
  const { slot, style, innerStyle, children, contentWidth } = props;
  return (
    <div slot={slot} style={{ display: 'flex', ...style }}>
      <span
        style={{
          ...alignRowRightStyle,
          minWidth: `${contentWidth || 100}px`,
          ...innerStyle,
        }}
      >
        {children}
      </span>
    </div>
  );
};

const resultsPerPageOptions: number[] = [10, 25, 50];

// TODO: Handle downloads in the future
export const CustomMEMTable = (props: Iprops) => {
  const {
    componentName,
    rowsData = [],
    dividedTextData,
    columnsInfo,
    filterKey,
    rowFormatter,
    isLoading,
    selectOnClick,
    selectType,
    rowSelectChange,
    selectAllChange,
    comandBar,
    showFilterBox = true,
  } = props;

  const pageName = usePageContext();
  const trackInteraction = useTrackInteraction();

  const [tableFilters, setTableFilters] = useState<{
    [key: string]: TTableFilters;
  }>({});
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [resultsPerPage, setResultsPerPage] = useState<number>(10);
  const [currPage, setCurrPage] = useState<number>(1);
  const [sortedBy, setSortedBy] = useState<string>();
  const [isAscending, setIsAscending] = useState<boolean>(true);
  const filteredRows = useFilteredRows(rowsData, filter, filterKey ? tableFilters[filterKey] : undefined);
  const sortedRows = useGetSortedRows(filteredRows, sortedBy, isAscending);
  const pageCount = useGetPageAmount(sortedRows, resultsPerPage);
  const displayedRows = useGetPageItems(sortedRows, currPage, resultsPerPage);
  const dummyLoadingRows = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        id: i + 1,
        cells: [],
      })) as unknown as Row[],
    []
  );

  const onPageChange = useCallback((event: any) => {
    setCurrPage(event.target.value);
  }, []);

  const onColumnSort = useCallback(
    (event: any) => {
      trackInteraction('Sort column', pageName, componentName, {
        SortBy: event.detail.sortBy,
        isAscending: event.detail,
      });
      setSortedBy(event.detail.sortBy);
      setIsAscending(event.detail.isAscending);
    },
    [componentName, pageName, trackInteraction]
  );

  const onResultsCountChange = useCallback(
    (event: any) => {
      trackInteraction('Result count changed', pageName, componentName, {
        Value: event.target.value,
      });
      setResultsPerPage(event.target.value);
    },
    [componentName, pageName, trackInteraction]
  );

  const handleFilter = useCallback(
    (e: any) => {
      trackInteraction('Applied filters', pageName, componentName, {
        filter: e.target.value,
      });
      setCurrPage(1);
      setFilter(e.target.value);
    },
    [componentName, pageName, trackInteraction]
  );

  const onAfterHide = useCallback((event: any) => {
    event.stopPropagation();
    setIsOpen(false);
  }, []);

  const onOpenTableFilterPanel = useCallback(() => {
    trackInteraction('Open Filter Panel', pageName, componentName);
    setIsOpen(true);
  }, [componentName, pageName, trackInteraction]);

  const handleApplyFilters = useCallback(
    (selectedFilterKey: string) => (newTableFilters: TTableFilters) => {
      // interaction tracked from panel
      setCurrPage(1);
      const newFilters: { [key: string]: TTableFilters } = { ...tableFilters };
      newFilters[selectedFilterKey] = newTableFilters;
      setTableFilters(newFilters);
      setIsOpen(false);
    },
    [tableFilters]
  );

  // Accessibility: scroll focused cell into view horizontally when cell focus changes
  const gridRef = React.useRef<any>(null);
  React.useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const shadowRoot = grid.shadowRoot;
    if (!shadowRoot) return;
    const handleFocus = (event: Event) => {
      const el = (event as FocusEvent).target as HTMLElement;
      if (!el) return;
      if (el.getAttribute('role') === 'columnheader' || el.getAttribute('role') === 'gridcell' || el.getAttribute('role') === 'rowheader') {
        el.scrollIntoView({ behavior: 'smooth', inline: 'end', block: 'nearest' });
        return;
      }
    };
    shadowRoot.addEventListener('focusin', handleFocus as EventListener);
    return () => {
      shadowRoot.removeEventListener('focusin', handleFocus as EventListener);
    };
  }, [rowsData, columnsInfo, currPage, sortedBy, isAscending, filter]);

  return (
    <>
      {filterKey && isOpen && (
        <TableFiltersPanel
          key={`${filterKey}-${componentName}`}
          open={isOpen}
          onHeAfterHide={onAfterHide}
          columns={columnsInfo}
          rows={rowsData}
          handleApplyFilters={handleApplyFilters(filterKey)}
          tableFilters={tableFilters[filterKey] || {}}
        />
      )}
      <DataGrid
        ref={gridRef}
        rows={isLoading ? dummyLoadingRows : displayedRows}
        columns={columnsInfo}
        onHeSort={onColumnSort}
        style={commonGridStyle}
        loading={isLoading}
        selectOnClick={selectOnClick}
        select={selectType}
        onHeRowSelectChange={rowSelectChange ? (event: any) => rowSelectChange(event.detail) : undefined}
        onHeSelectAllChange={selectAllChange ? (event: any) => selectAllChange(event.detail) : undefined}
      >
        <div slot="data-grid-controls" style={{ display: 'flex' }}>
          <div style={{ flex: 2 }}>
            {comandBar}
            {dividedTextData && <DividerSeparatedText data={dividedTextData} />}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            {filterKey && (
              <Button style={{ marginRight: '10px' }} appearance="command" onClick={onOpenTableFilterPanel}>
                <>
                  <Icon slot="start" name="filter" />
                  {t('filter')}
                </>
              </Button>
            )}
            {showFilterBox && (
              <SearchBox style={searchBoxStyle} onHeInput={handleFilter} onHeClear={handleFilter} placeholder="Search" label="Search" hideLabel />
            )}
          </div>
        </div>
        {rowFormatter && displayedRows.map((row) => rowFormatter(row))}
        {pageCount > 1 && (
          <div
            slot="pagination"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {`${rowsData.length} ${t('results')}`}
            <Pagination style={{ flexGrow: 1 }} pageCount={pageCount} onHePageChange={onPageChange} />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text tag="span" style={{ padding: '.5rem' }}>{`${t('resultsPerPage')} `}</Text>
              <Combobox
                fixed-placement
                onHeChange={onResultsCountChange}
                value={resultsPerPage}
                className={paginatorCombobox}
                label={`${t('resultsPerPage')} `}
                hideLabel
              >
                {resultsPerPageOptions.map((num) => (
                  <Option key={num} value={num}>
                    {num}
                  </Option>
                ))}
              </Combobox>
            </div>
          </div>
        )}
      </DataGrid>
    </>
  );
};
